import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { buildSharedRecordTabsFromRecords } from 'components/patient/buildSharedRecordTabs';
import StandardModal from 'components/shared/StandardModal';
import LocationEpisode from 'models/LocationEpisode';
import Profile from 'models/Profile';
import { useSharedRecordTabsActions } from 'stores/sharedRecordTabsStore';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { PatientCard } from './PortfolioCard';

type SharedRecordSelectionModalProps = {
  records: LocationEpisode[];
  profile: Profile;
  onClose: () => void;
};

export default function SharedRecordSelectionModal({ records, profile, onClose }: SharedRecordSelectionModalProps) {
  const navigate = useNavigate();
  const { setTabs } = useSharedRecordTabsActions();

  const handleSelect = (record: LocationEpisode) => {
    onClose();
    setTabs(buildSharedRecordTabsFromRecords(records), record.id);
    navigate(`/patients/${record.episodeId}?locationEpisodeId=${record.id}`);
  };

  return (
    <StandardModal title='Select Patient Record' onCancel={onClose} closeOnBackdropClick>
      <Prompt>
        <BodySmallBold as='span'>This patient is linked to {records.length} records in Olio.</BodySmallBold> View
        associated details for each record and know what team in Olio is receiving updates.
      </Prompt>
      <RecordList>
        {[...records]
          .sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''))
          .map((record) => (
            <RecordRow key={record.id} onClick={() => handleSelect(record)}>
              <PatientCard locationEpisode={record} profile={profile} invalidateColumn={() => {}} />
            </RecordRow>
          ))}
      </RecordList>
    </StandardModal>
  );
}

const Prompt = styled(BodySmall)`
  color: ${colors.black75};
  margin-bottom: 24px;
`;

const RecordList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RecordRow = styled.div`
  cursor: pointer;
  border-radius: 6px;
  border: 1px solid ${colors.black10};
  overflow: hidden;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${colors.blue100};
  }
`;
